<template>
  <div class="auth-split">
    <main class="auth-panel">
      <div class="auth-panel-inner">
        <router-link
          :to="{ name: 'home' }"
          class="auth-logo"
          aria-label="Sonata home"
        >
          <img
            src="@/assets/sonata-logo.svg"
            width="112"
            height="30"
            alt="Sonata"
          >
        </router-link>
        <div class="auth-body">
          <slot />
        </div>
      </div>
    </main>
    <aside
      class="auth-art"
      aria-label="Featured Sonata artist"
    >
      <img
        src="@/assets/auth-artist-olivia.jpg"
        alt="Olivia Reid standing among white oleander flowers"
      >
      <span class="auth-caption">Artist: Olivia Reid</span>
    </aside>
  </div>
</template>

<script>
export default {
  name: 'AuthSplitLayout'
}
</script>

<style lang="scss">
@import "../assets/scss/_variables.scss";
@import "../assets/scss/_mixins.scss";

$auth-red: rgb(214, 64, 64);
$auth-red-ink: rgb(196, 52, 52);
$auth-red-pale: #fdf0f0;
$auth-green: rgb(34, 160, 92);

// Unscoped so the page views can share these styles; everything is namespaced
// under .auth-split.
.auth-split {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  min-height: 100vh;
  // Cancel #app's padding, which leaves room for the header and player.
  margin: -120px 0 -160px;
  background: $white;
  color: $bluegrey3;
  font-size: 14px;

  .auth-panel {
    display: flex;
    flex-direction: column;
    padding: 40px 16px 48px;
  }

  .auth-panel-inner {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 28px;
    width: 100%;
    max-width: 400px;
    margin: 0 auto;
  }

  .auth-logo {
    display: block;
    width: 112px;
    img {
      display: block;
    }
  }

  .auth-body {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin: auto 0;
    padding: 24px 0;
  }

  .auth-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  h1 {
    margin: 0;
    color: $bluegrey1;
    font-weight: 500;
    font-size: 44px;
    line-height: 1.1;
    letter-spacing: -0.01em;
  }

  a {
    color: $bluegrey1;
  }

  .auth-switch {
    margin: 10px 0 0;
    font-size: 15px;
    a {
      font-weight: 500;
    }
  }

  .auth-form {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .auth-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .auth-field-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    a {
      font-size: 13px;
      color: $bluegrey3;
    }
  }

  label {
    color: $bluegrey2;
    font-size: 14px;
  }

  .auth-control {
    position: relative;
  }

  .auth-input {
    width: 100%;
    height: 50px;
    padding: 0 14px;
    border: 1px solid transparent;
    border-radius: 4px;
    background: $bluegrey7;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
    font: 16px $family-sans-serif;
    color: $bluegrey1;
    &::placeholder {
      color: $bluegrey4;
    }
    &:focus {
      outline: none;
      border-color: $bluegrey1;
      background: $white;
    }
    &.has-error {
      border-color: $auth-red;
      background: $auth-red-pale;
    }
  }

  .has-toggle .auth-input {
    padding-right: 52px;
  }

  .auth-reveal {
    position: absolute;
    top: 4px;
    right: 4px;
    display: grid;
    place-items: center;
    width: 42px;
    height: 42px;
    border: 0;
    border-radius: 4px;
    background: none;
    color: $bluegrey3;
    cursor: pointer;
    &:hover {
      color: $bluegrey1;
    }
    svg {
      width: 20px;
      height: 20px;
    }
  }

  .auth-error {
    margin: 0;
    color: $auth-red-ink;
    font-size: 13px;
  }

  .auth-message {
    margin: 0;
    padding: 12px 14px;
    border-radius: 4px;
    background: $auth-red-pale;
    color: $auth-red-ink;
    font-size: 14px;
  }

  .auth-rules {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 2px 0 0;
    padding: 0;
    list-style: none;
    font-size: 13px;
    li {
      display: flex;
      align-items: center;
      gap: 8px;
      color: $bluegrey3;
      transition: color 0.15s;
      &::before {
        content: "";
        flex: none;
        width: 14px;
        height: 14px;
        border: 1.5px solid $bluegrey5;
        border-radius: 50%;
        transition: all 0.15s;
      }
      &.is-ok {
        color: $bluegrey1;
        &::before {
          border-color: $auth-green;
          background: $auth-green url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Cpath d='M3.5 7.2l2.2 2.2 4.8-4.8' fill='none' stroke='white' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center / 12px no-repeat;
        }
      }
    }
  }

  .auth-check {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 13px;
    line-height: 1.5;
    color: $bluegrey2;
    cursor: pointer;
    input {
      flex: none;
      width: 18px;
      height: 18px;
      margin: 1px 0 0;
      accent-color: $bluegrey0;
      cursor: pointer;
    }
  }

  .auth-submit {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    height: 50px;
    padding: 0 24px;
    border: 0;
    border-radius: 999px;
    background: $accent;
    color: $white;
    font: 500 15px $family-sans-serif;
    cursor: pointer;
    transition: background 0.12s ease-out;
    &:hover {
      background: darken($accent, 6%);
    }
    &[disabled] {
      opacity: 0.6;
      cursor: progress;
    }
  }

  .auth-spin {
    width: 16px;
    height: 16px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: auth-spin 0.7s linear infinite;
  }

  .auth-legal {
    margin: 0;
    font-size: 12px;
    line-height: 1.6;
    color: $bluegrey3;
    a {
      color: $bluegrey2;
      text-decoration: underline;
    }
  }

  .auth-done {
    display: flex;
    flex-direction: column;
    gap: 20px;
    p {
      margin: 0;
      font-size: 16px;
      line-height: 1.5;
      color: $bluegrey2;
    }
    a {
      text-decoration: underline;
    }
    a.auth-continue {
      display: inline-flex;
      align-self: flex-start;
      align-items: center;
      gap: 10px;
      height: 46px;
      margin-top: 8px;
      padding: 0 24px 0 28px;
      border: 1.5px solid $bluegrey1;
      border-radius: 999px;
      color: $bluegrey1;
      font: 500 15px $family-sans-serif;
      text-decoration: none;
      transition: background 0.12s ease-out;
      svg {
        transition: transform 0.12s ease-out;
      }
      &:hover {
        background: $bluegrey7;
        svg {
          transform: translateX(2px);
        }
      }
    }
  }

  .auth-tick {
    display: grid;
    place-items: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: $bluegrey7;
    color: $auth-green;
  }

  .auth-eyebrow {
    @include uppercase();
    font-size: 12px;
    color: $bluegrey3;
  }

  a:focus-visible,
  button:focus-visible,
  input:focus-visible {
    outline: 2px solid $bluegrey1;
    outline-offset: 2px;
  }

  .auth-art {
    position: relative;
    overflow: hidden;
    background: $bluegrey1;
    img {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      max-width: none;
      object-fit: cover;
      object-position: 50% 35%;
    }
    &::after {
      content: "";
      position: absolute;
      inset: auto 0 0;
      height: 40%;
      background: linear-gradient(to top, rgba(6, 11, 18, 0.55), rgba(6, 11, 18, 0));
    }
  }

  .auth-caption {
    @include uppercase();
    position: absolute;
    z-index: 1;
    left: 40px;
    right: 40px;
    bottom: 36px;
    color: $white;
    font-size: 16px;
    font-weight: 400 !important;
  }

  @media (max-width: 860px) {
    grid-template-columns: minmax(0, 1fr);
    .auth-art {
      display: none;
    }
    h1 {
      font-size: 36px;
    }
    .auth-panel {
      padding: 24px 16px 32px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .auth-spin {
      animation-duration: 2s;
    }
    * {
      transition: none !important;
    }
  }
}

@keyframes auth-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
